'use client'

import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { isLabPerformanceAgent } from '@/lib/defer-third-party'
import { showRewardedAd } from '@/lib/gpt-ads'
import {
  getOfferwallAccessMinutes,
  getPublicSiteConfig,
  isOfferwallEnabled,
  resolveRewardedGamAdUnitPath,
} from '@/lib/public-site-config'

/**
 * Offerwall (ödüllü reklam) kapısı — resmî GPT rewarded ad akışıyla çalışır.
 *
 * - Erişim yoksa içeriğin başını gösterir, devamı için "Reklam İzle" çıkarır.
 * - Ödül kazanılırsa erişim localStorage'a yazılır (varsayılan 30 dk,
 *   admin panelinden değiştirilebilir) ve süre boyunca kapı tekrar çıkmaz.
 * - Reklam ödülsüz kapatılırsa içerik kilitli kalır.
 * - Doluluk yok / ad blocker / GPT hatası: kullanıcı cezalandırılmaz,
 *   içerik açılır (fail-open) ancak erişim kaydedilmez.
 * - Bot / Lighthouse / SSR: içerik her zaman açık (SEO güvenli); içerik
 *   DOM'da kalır, kilit yalnızca görseldir.
 *
 * Kullanım: <OfferwallGate><PremiumContent /></OfferwallGate>
 */

const STORAGE_KEY = 'haber:offerwall:accessUntil'

type GateStatus = 'checking' | 'unlocked' | 'locked' | 'loading'

function readAccessValid(): boolean {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return false
    const until = Number(raw)
    if (!Number.isFinite(until) || until <= Date.now()) {
      window.localStorage.removeItem(STORAGE_KEY)
      return false
    }
    return true
  } catch {
    // localStorage kapalıysa (gizli mod vb.) kilitleme yapma
    return true
  }
}

function grantAccess(minutes: number): void {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      String(Date.now() + minutes * 60_000),
    )
  } catch {
    /* ignore */
  }
}

export default function OfferwallGate({
  children,
  suppressed = false,
}: {
  children: ReactNode
  /** Başka bir kapı (örn. paywall) aktifken çifte kilidi önlemek için */
  suppressed?: boolean
}) {
  const [status, setStatus] = useState<GateStatus>('checking')
  const [adUnitPath, setAdUnitPath] = useState('')
  const [accessMinutes, setAccessMinutes] = useState(30)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (suppressed || isLabPerformanceAgent()) {
      setStatus('unlocked')
      return
    }
    if (readAccessValid()) {
      setStatus('unlocked')
      return
    }
    getPublicSiteConfig()
      .then((cfg) => {
        if (cancelled) return
        if (!isOfferwallEnabled(cfg)) {
          setStatus('unlocked')
          return
        }
        const path = resolveRewardedGamAdUnitPath(cfg)
        if (!path) {
          setStatus('unlocked')
          return
        }
        setAdUnitPath(path)
        setAccessMinutes(getOfferwallAccessMinutes(cfg))
        setStatus('locked')
      })
      .catch(() => {
        if (!cancelled) setStatus('unlocked')
      })
    return () => {
      cancelled = true
    }
  }, [suppressed])

  const watchAd = useCallback(async () => {
    setErrorMsg(null)
    setStatus('loading')
    const result = await showRewardedAd(adUnitPath)
    if (result === 'granted') {
      grantAccess(accessMinutes)
      setStatus('unlocked')
      return
    }
    if (result === 'closed') {
      setErrorMsg(
        'Reklam tamamlanmadı. İçeriğe erişmek için reklamı sonuna kadar izleyin.',
      )
      setStatus('locked')
      return
    }
    // unfilled / error: reklam gösterilemedi (doluluk yok, ad blocker, GPT
    // hatası) — kullanıcıyı içerikten mahrum bırakma, erişim kaydetmeden aç
    setStatus('unlocked')
  }, [adUnitPath, accessMinutes])

  if (status === 'unlocked') return <>{children}</>

  if (status === 'checking') {
    // Erişim kontrolü çok kısa sürer; içerik gizli tutulur, CLS önlemek için
    // kilitli görünümle aynı yükseklik davranışı kullanılır
    return (
      <div className="relative">
        <div className="max-h-96 overflow-hidden" aria-hidden>
          {children}
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-slate-950 dark:via-slate-950/95" />
      </div>
    )
  }

  const loading = status === 'loading'

  return (
    <div className="relative">
      <div className="max-h-96 overflow-hidden">{children}</div>
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-slate-950 dark:via-slate-950/95"
        aria-hidden
      />
      <div className="relative -mt-10 mb-8 rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-lg sm:p-8 dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto mb-3 flex size-11 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400">
          <svg viewBox="0 0 24 24" fill="none" className="size-5" aria-hidden>
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14.75 10.5l-4.5-2.6a.5.5 0 00-.75.44v5.32a.5.5 0 00.75.43l4.5-2.59a.5.5 0 000-.87zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-gray-900 sm:text-xl dark:text-white">
          Haberin devamı için kısa bir reklam izleyin
        </h2>
        <p className="mx-auto mt-1.5 max-w-md text-sm text-gray-600 dark:text-slate-400">
          Ödüllü reklamı izleyerek bu içeriğe ve tüm haberlere{' '}
          {accessMinutes} dakika boyunca kesintisiz erişin.
        </p>
        {errorMsg && (
          <p className="mx-auto mt-2 max-w-md text-sm font-medium text-red-600 dark:text-red-400">
            {errorMsg}
          </p>
        )}
        <div className="mt-5 flex justify-center">
          <button
            type="button"
            onClick={watchAd}
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
          >
            {loading && (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="size-4 animate-spin"
                aria-hidden
              >
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  strokeOpacity="0.3"
                  strokeWidth="3"
                />
                <path
                  d="M21 12a9 9 0 00-9-9"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="3"
                />
              </svg>
            )}
            {loading ? 'Reklam yükleniyor…' : 'Reklamı İzle ve Devamını Oku'}
          </button>
        </div>
      </div>
    </div>
  )
}
